import React, { useRef } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconNames, InlineSVG, Pill } from '@dotcom/crown-ui';
import classNames from 'classnames';
import { useCropContext } from '../../context/crop-context';
import { useInitializationDataContext } from '../../context/data-context';
import { RegionalCodesList } from '../../global-types';
import { useEnabledRoutes, useHeaderClick, useSaveProjectDialog } from '../../hooks';
import { useIsOneToMany } from '../../hooks/use-is-one-to-many';
import { config } from '../../regional-config';
import { isLoggedIn } from '../../utils';
import { Quantity } from '../quantity/quantity';
import { SaveProjectDialog } from '../save-project-dialog';
import { SaveProjectLink } from '../save-project-link';
import { PrintOnDemandHeaderProps } from './print-on-demand-header-types';
import styles from './print-on-demand-header.module.scss';

const PrintOnDemandHeader = ({ onClose, isEditor, onReturnToHomePage, isUK }: PrintOnDemandHeaderProps) => {
  const { isEnabledRoutes } = useEnabledRoutes();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const { initializedDataState } = useInitializationDataContext();
  const { region } = initializedDataState;
  const {
    saveProjects: { isSavedProjectsEnabled },
  } = config;
  const classes = classNames(styles.header, isEditor && styles.editor);
  const headerParentRef = useHeaderClick();

  const {
    isSaveProjectDialogOpen,
    isAbandoningProject,
    onCloseDialog,
    onSignIn,
    onAbandonProject,
    onSaveProject,
    setIsExitFromXButton,
  } = useSaveProjectDialog();

  const { isCropping } = useCropContext();

  // TODO: This should be re-added once the project summary is implemented for 1:1 cards (PRCM-3032)
  const showProjectSummary = useIsOneToMany();
  const hasEnvelope = isEnabledRoutes(['envelope', 'address']);
  const isBetaPillVisible = showProjectSummary && hasEnvelope;

  const onCrownIconClick = () => {
    if (isCropping) {
      return;
    }
    setIsExitFromXButton(false);
    onReturnToHomePage();
  };

  const onCloseButtonClick = () => {
    setIsExitFromXButton(true);
    onClose(closeButtonRef.current);
  };
  const isSaveProjectLinkEnabled = isEnabledRoutes(['/edit/']) && isSavedProjectsEnabled;
  return (
    <div className={styles['print-on-demand-header-container']}>
      <div className={classes} ref={headerParentRef}>
        {showProjectSummary ? <Quantity /> : <div className={styles['quantity-link-placeholder']} />}
        <Button onClick={onCrownIconClick} className={styles.crown} testId="crown-icon-button" tabIndex={0}>
          <InlineSVG
            title={`${t('header.hallmarkCrownLogo')}`}
            description={`${t('header.hallmarkCrownLogo')}`}
            role="img"
            className={isUK ? styles['crown-logo-color-uk'] : undefined}
          />
        </Button>
        <div className={`${styles['save-project']}`}>
          {isSaveProjectLinkEnabled && !isCropping ? <SaveProjectLink /> : <div className={styles['save-project']} />}

          {!isCropping && (
            <Button
              className={styles['close-button']}
              size="icon"
              variant="secondary"
              testId="close-icon-button"
              onClick={onCloseButtonClick}
              ref={closeButtonRef}
              aria-label={`${t('header.close')}`}
              alt-text={`${t('header.closeButton')}`}
            >
              <Icon name={IconNames.Menuclose} size={!isMobile ? '28' : '24'} />
            </Button>
          )}
        </div>

        {isSaveProjectDialogOpen && (
          <SaveProjectDialog
            isOpen={isSaveProjectDialogOpen}
            close={onCloseDialog}
            isLoggedIn={isLoggedIn()}
            isAbandoningProject={isAbandoningProject}
            onSaveProject={onSaveProject}
            onSignIn={onSignIn}
            onAbandonProject={onAbandonProject}
            theme={region || RegionalCodesList.us} // region will fallback to 'us' if process.env.REACT_APP_THEME is not set
          />
        )}
      </div>
      {isBetaPillVisible && (
        <div className={styles['beta-label-container']}>
          <Pill title="Beta" className={styles['beta-label']} />
        </div>
      )}
    </div>
  );
};

const MemoizedPrintOnDemandHeader = React.memo(PrintOnDemandHeader);

export { MemoizedPrintOnDemandHeader as PrintOnDemandHeader };
